import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';

import {
  IconHash,
  IconEdit,
  IconStar,
  IconFolders,
  IconCategory,
  IconFileUpload,
} from '@tabler/icons-react';

import CustomModal from '@/components/CustomModal';
import Select from '@/components/Select';
import { Switch } from '@/components/Switch';
import TextArea from '@/components/TextArea';
import {
  LessonStageConfig,
  ContentStatusConfig,
  LESSON_STAGE_OPTIONS,
} from '@/constants/constant';
import { EContentStatus, ELessonStageType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import {
  updateContent,
  getContentDetails,
  uploadPreviewImage,
  updateContentStatus,
} from '@/service/contentService';
import { IContentGameData } from '@/types';
import { formatSecondsToDuration } from '@/utils';

interface IGameDetailsModalProps {
  setIsOpen: (open: boolean) => void;
  game: IContentGameData | null;
  onSuccess?: () => void;
}

const GameDetailsModal = ({
  setIsOpen,
  game,
  onSuccess,
}: IGameDetailsModalProps) => {
  const { formatText } = useFormatText();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [localGame, setLocalGame] = useState<IContentGameData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRestrictLoading, setIsRestrictLoading] = useState(false);

  // Edit form state
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ELessonStageType | ''>('');
  const [isActiveEdit, setIsActiveEdit] = useState(false);
  const [quarters, setQuarters] = useState('');
  const [rounds, setRounds] = useState<number | ''>('');
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileSizeError, setFileSizeError] = useState<string | null>(null);

  const fetchDetails = async () => {
    if (!game?.id) return;
    setIsLoading(true);
    try {
      const res = await getContentDetails(game.id);
      if (res?.status && res?.data) {
        setLocalGame(res?.data);
        // Initialize edit states
        setDescription(res?.data?.description ?? '');
        setCategory(res?.data?.category ?? '');
        setIsActiveEdit(res?.data?.status === EContentStatus.ACTIVE);
        setPreviewUrl(res?.data?.preview_image ?? null);
        setQuarters(res?.data?.quarters ?? '');
        setRounds(res?.data?.rounds ?? '');
      }
    } catch (error) {
      console.error('Error fetching game details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.id]);

  const handleRestrictToggle = async (restrict: boolean) => {
    const activeGame = localGame || game;
    if (!activeGame?.id) return;
    setIsRestrictLoading(true);
    try {
      const res = await updateContentStatus(activeGame.id, !restrict);
      if (res?.status) {
        onSuccess?.();
        await fetchDetails();
      }
    } catch (error) {
      console.error('Error updating game status:', error);
    } finally {
      setIsRestrictLoading(false);
    }
  };

  const resetState = () => {
    setIsEditing(false);
    setLocalGame(null);
    setDescription('');
    setCategory('');
    setIsActiveEdit(false);
    setQuarters('');
    setRounds('');
    setPreviewFile(null);
    setPreviewUrl(null);
    setIsDragging(false);
    setFileSizeError(null);
  };

  const handleClose = () => {
    resetState();
    setIsOpen(false);
  };

  const handleSave = async () => {
    const activeGame = localGame || game;
    if (!activeGame?.id) return;

    setIsSaving(true);
    try {
      let assetId = undefined;

      if (previewFile) {
        const uploadRes = await uploadPreviewImage(previewFile);
        if (uploadRes?.status && uploadRes?.data) {
          assetId = uploadRes?.data?.id;
        }
      }

      const payload: Record<string, unknown> = {
        description,
        status: isActiveEdit,
        category: category,
        quarters: quarters,
        rounds: rounds,
      };

      if (assetId) {
        payload.asset_id = assetId;
      }

      const res = await updateContent(activeGame.id, payload);
      if (res?.status) {
        setIsEditing(false);
        onSuccess?.();
        await fetchDetails();
      }
    } catch (error) {
      console.error('Error updating game:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Fallback to initial game prop while loading or if fetch fails
  const activeGame = localGame || game;

  if (!activeGame) return null;

  // View-only derived values
  const stageConfig =
    LessonStageConfig[
      (Number(activeGame?.category) || activeGame?.category) as ELessonStageType
    ];
  const statusConfig =
    ContentStatusConfig[activeGame?.status as keyof typeof ContentStatusConfig];
  const isActive = activeGame?.status === EContentStatus.ACTIVE;

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

  const applyFile = (file: File) => {
    if (file?.size > MAX_FILE_SIZE) {
      setFileSizeError(
        formatText('contentManagement.edit-modal.file-size-error')
      );
      return;
    }
    setFileSizeError(null);
    setPreviewFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) applyFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) applyFile(file);
  };

  const headerContent = isEditing ? (
    <div className="flex items-center gap-2 min-w-0">
      <h2 className="text-neutral-primary font-medium leading-7 text-lg truncate capitalize">
        {formatText('contentManagement.edit-modal.title')}{' '}
        {activeGame?.title ?? '-'}
      </h2>
    </div>
  ) : (
    <div className="flex items-center gap-2 min-w-0 justify-between w-full pe-4 ">
      <h2 className="text-neutral-primary font-medium leading-7 text-lg truncate capitalize">
        {activeGame?.title ?? '-'}
      </h2>
      <button
        className="p-1.5 rounded-md bg-neutral-100 text-neutral-secondary shrink-0 cursor-pointer"
        onClick={() => setIsEditing(true)}
      >
        <IconEdit size={18} />
      </button>
    </div>
  );

  return (
    <CustomModal
      setIsOpen={handleClose}
      isHeader
      isFooter
      position="right"
      headercontent={headerContent}
      loadingModal={isLoading || isSaving}
      confirmButtonProps={{
        label: isEditing
          ? formatText('contentManagement.edit-modal.save')
          : formatText('common.actions.done'),
        onClick: async () => {
          if (isEditing) {
            await handleSave();
          } else {
            handleClose();
          }
        },
        className: 'ms-auto',
      }}
      cancelButtonProps={
        isEditing
          ? {
              label: formatText('common.actions.cancel'),
              onClick: () => setIsEditing(false),
              variant: 'link',
            }
          : undefined
      }
    >
      <div className={clsx('w-full', isEditing ? 'space-y-6' : 'space-y-10')}>
        {isEditing ? (
          <>
            {/* Overview */}
            <section className="space-y-2">
              <h3 className="text-lg font-semibold text-neutral-primary">
                {formatText('contentManagement.modal.overview')}
              </h3>
              <TextArea
                name="description"
                placeholder={formatText(
                  'contentManagement.edit-modal.description-placeholder'
                )}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={250}
                showCount
                rows={4}
                disabled
              />
            </section>
            {/* Details */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-neutral-primary">
                {formatText('contentManagement.edit-modal.details')}
              </h3>

              {/* Stage */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-neutral-primary">
                  {formatText('contentManagement.modal.stage')}
                </label>
                <Select
                  options={LESSON_STAGE_OPTIONS}
                  selected={String(category)}
                  onChange={(val) =>
                    setCategory(val as unknown as ELessonStageType)
                  }
                  placeholder={formatText(
                    'contentManagement.edit-modal.stage-placeholder'
                  )}
                  isTextFormat
                />
              </div>

              {/* Status */}
              <div>
                <label className="text-sm font-medium text-neutral-primary">
                  {formatText('contentManagement.modal.status')}
                </label>
                <div className="flex items-center gap-2 mt-4">
                  <Switch
                    id="edit-game-status"
                    checked={isActiveEdit}
                    onChange={setIsActiveEdit}
                  />
                  <span className="text-sm text-neutral-primary">
                    {formatText('contentManagement.modal.status')}:{' '}
                    <span className={clsx('font-medium')}>
                      {isActiveEdit
                        ? formatText('contentManagement.table.status.active')
                        : formatText('contentManagement.table.status.inactive')}
                    </span>
                  </span>
                </div>
              </div>
            </section>
            {/* Preview Image */}
            <section className="space-y-2">
              <h3 className="text-lg font-semibold text-neutral-primary">
                {formatText('contentManagement.edit-modal.preview-image')}
              </h3>

              {previewUrl ? (
                <div className="relative rounded-xl border border-neutral-light-200 p-2">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-75 object-cover rounded-lg cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  />
                </div>
              ) : (
                <div
                  className={clsx(
                    'rounded-xl p-8 flex flex-col items-center justify-center gap-4 cursor-pointer transition-colors',
                    isDragging
                      ? 'border-brand-default bg-brand-light'
                      : 'border-neutral-light-200 hover:border-neutral-300'
                  )}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <IconFileUpload size={24} className="text-neutral-400" />

                  <div className="space-y-1">
                    <p className="text-sm font-medium text-neutral-primary text-center">
                      {formatText('contentManagement.edit-modal.upload-title')}
                    </p>

                    <p className="text-sm text-neutral-secondary text-center">
                      {formatText('contentManagement.edit-modal.upload-desc')}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-sm font-medium text-brand-default hover:underline bg-brand-light-50 py-1.5 px-3 rounded-lg"
                    onClick={(e) => {
                      e.stopPropagation();
                      fileInputRef.current?.click();
                    }}
                  >
                    <IconFolders size={16} />
                    {formatText('contentManagement.edit-modal.browse-files')}
                  </button>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.mp4"
                className="hidden"
                onChange={handleFileChange}
              />
              {fileSizeError && (
                <p className="text-xs text-error-secondary">{fileSizeError}</p>
              )}
            </section>
          </>
        ) : (
          <>
            {/* Overview */}
            <section>
              <h3 className="text-base font-semibold text-neutral-primary">
                {formatText('contentManagement.modal.overview')}
              </h3>

              {/* Description */}
              <p className="text-sm text-neutral-secondary leading-5 mt-4">
                {activeGame?.description ||
                  formatText('contentManagement.modal.preview-placeholder')}
              </p>

              {/* Info grid */}
              <div className="grid grid-cols-2 gap-4 mt-10">
                {/* Category/Stage */}
                <div className="flex items-start gap-3">
                  <IconStar
                    size={16}
                    className="text-neutral-hover mt-0.5 shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-neutral-secondary">
                      {formatText('contentManagement.modal.stage')}
                    </span>
                    <span className="text-sm font-medium text-neutral-primary capitalize">
                      {stageConfig ? formatText(stageConfig.label) : '-'}
                    </span>
                  </div>
                </div>
                {/* Status toggle */}

                <div className="flex flex-col min-w-0">
                  <span className="text-sm text-neutral-secondary leading-5">
                    {formatText('contentManagement.modal.status')}
                  </span>
                  <div className="flex gap-2">
                    <Switch
                      id="game-status"
                      checked={isActive}
                      disabled={isRestrictLoading}
                      onChange={() => handleRestrictToggle(!isActive)}
                    />
                    <span className="text-sm text-neutral-primary leading-5 font-medium">
                      {statusConfig
                        ? formatText(statusConfig.label)
                        : activeGame?.status}
                    </span>
                  </div>
                </div>

                {/* Quarter */}
                <div className="flex items-start gap-3">
                  <IconCategory
                    size={16}
                    className="text-neutral-hover mt-0.5 shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-neutral-secondary">
                      {formatText('contentManagement.modal.quarter')}
                    </span>
                    <span className="text-sm font-medium text-neutral-primary">
                      {activeGame?.quarters ?? '-'}
                    </span>
                  </div>
                </div>

                {/* Round */}
                <div className="flex items-start gap-3">
                  <IconHash
                    size={16}
                    className="text-neutral-hover mt-0.5 shrink-0"
                  />
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm text-neutral-secondary">
                      {formatText('contentManagement.modal.round')}
                    </span>
                    <span className="text-sm font-medium text-neutral-primary">
                      {activeGame?.rounds ?? '-'}
                    </span>
                  </div>
                </div>
              </div>
            </section>

            {/* Preview */}
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-neutral-primary">
                {formatText('contentManagement.modal.preview')}
              </h3>

              {activeGame?.preview_image ? (
                <div className="w-full h-75 rounded-xl overflow-hidden">
                  <img
                    src={activeGame?.preview_image}
                    alt={activeGame?.title ?? '-'}
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
              ) : (
                <p className="text-lg text-neutral-secondary text-center p-6">
                  {formatText('contentManagement.modal.preview-placeholder')}
                </p>
              )}
            </section>

            {/* Analytics */}
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-neutral-primary">
                {formatText('contentManagement.modal.analytics')}
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-warning-light-100 px-4 py-2 flex flex-col gap-2">
                  <div className="flex items-center justify-center gap-1.5">
                    <span className="text-xs font-medium text-warning-secondary">
                      {formatText('contentManagement.modal.avg-play-time')}
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-warning-primary text-center">
                    {activeGame?.avg_play_time !== undefined
                      ? formatSecondsToDuration(activeGame?.avg_play_time)
                      : '-'}
                  </span>
                </div>
                <div className="rounded-xl bg-success-light-50 px-4 py-2 flex flex-col gap-2">
                  <div className="flex items-center justify-center gap-1.5 text-success-secondary">
                    <span className="text-xs font-medium">
                      {formatText('contentManagement.modal.completion-rate')}
                    </span>
                  </div>
                  <span className="text-2xl font-bold text-success-primary text-center">
                    {activeGame?.completion_rate !== undefined
                      ? `${activeGame?.completion_rate}%`
                      : '-'}
                  </span>
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default GameDetailsModal;
